import { ElementRef, Injectable, computed, inject, signal } from '@angular/core';
import {
  EditSectionSelection,
  SectionId,
  SectionType,
} from '../../../../features/invoice-theme/theme.types';
import {
  isHiddenMeta,
  isRemovedMeta,
} from '../../../constants-repository/edit-document-sections.constants';
import { dataDynamicFieldMetaKey } from '../../../../features/invoice-theme/theme.constants';
import { KeyValueRecord, findKvRecordValue } from '../../../../shared/functions/record-functions';
import { DateTimeHelper } from '../../../../features/calendar/util/date-time-helper';
import { ShiveRenderService } from '../../rendering/shive-render.service';

@Injectable({ providedIn: 'root' })
export class EditDocumentAreaService {
  // Used for initial content area editing (i.e. initialize sidebar WYSIWYG editor, image editor, etc.) and
  // for highlighting the selected content area.
  public readonly selected = computed(() => this._selected());
  // Used if a user hovers a section
  public readonly hovered = signal<SectionId | null>(null);

  private readonly _selected = signal<EditSectionSelection>({
    type: SectionType.Undefined,
    sectionId: null,
  });

  // DI
  private readonly shiveRenderService = inject(ShiveRenderService);

  public set(selection: EditSectionSelection): void {
    this._selected.set(selection);
  }

  public clear(): void {
    this._selected.set({
      type: SectionType.Undefined,
      sectionId: null,
    });
  }

  public hover(id: SectionId | null): void {
    this.hovered.set(id);
  }

  public isHidden(meta: Record<string, unknown>): boolean {
    return meta ? !!meta[isHiddenMeta] && meta[isHiddenMeta] === 'true' : false;
  }

  public isRemoved(meta: Record<string, unknown>): boolean {
    return meta ? !!meta[isRemovedMeta] && meta[isRemovedMeta] === 'true' : false;
  }

  public fillDynamicFields(meta: KeyValueRecord[], el: ElementRef<HTMLElement>): void {
    if (!meta) {
      return;
    }

    const host = el.nativeElement;
    const spans = host.querySelectorAll(`[${dataDynamicFieldMetaKey}]`);

    spans.forEach((span) => {
      const key = span.getAttribute(dataDynamicFieldMetaKey);
      if (!key) {
        return;
      }

      let value = findKvRecordValue(meta, key);

      const maybeDate = DateTimeHelper.parseISO(value as string);
      if (DateTimeHelper.isValid(maybeDate)) {
        value = DateTimeHelper.format(maybeDate);
      }

      // Clear previous content and set new text safely
      this.shiveRenderService.setProperty(span, 'textContent', value);
    });
  }
}
